import { Texture } from 'pixi.js';

export interface TrailSkin {
  id: string;
  name: string;
  cost: number;
  outer: number;
  mid: number;
  core: number;
}

export interface TrailTextureSet {
  outer: Texture;
  mid: Texture;
  core: Texture;
}

const TRAIL_TEX_CACHE: Map<string, TrailTextureSet> = new Map();

// Bake a horizontal gradient strip: alpha fades head(left)→tail(right),
// vertical bell curve concentrates brightness at the rope center.
// Pixi MeshRope maps texture width along the rope's length and texture height
// is the perpendicular thickness — so height = ribbon thickness in world px.
function bakeStrip(width: number, height: number): Texture {
  const c = document.createElement('canvas');
  c.width = width;
  c.height = height;
  const ctx = c.getContext('2d')!;
  const img = ctx.createImageData(width, height);
  const data = img.data;
  const halfH = (height - 1) / 2;
  for (let y = 0; y < height; y++) {
    // bell curve across thickness: center=1, edges→0 (ease-in-out)
    const yn = (y - halfH) / halfH; // -1..1
    const yfall = Math.max(0, 1 - yn * yn); // parabolic 0..1
    const yEased = yfall * yfall * (3 - 2 * yfall); // smoothstep
    for (let x = 0; x < width; x++) {
      // alpha falloff head→tail: head bright, tail fades to 0
      const xn = x / (width - 1); // 0..1
      const xfall = 1 - xn;
      const xEased = xfall * xfall; // quadratic — sharper head, longer ghost tail
      const a = Math.round(255 * yEased * xEased);
      const idx = (y * width + x) * 4;
      data[idx] = 255;
      data[idx + 1] = 255;
      data[idx + 2] = 255;
      data[idx + 3] = a;
    }
  }
  ctx.putImageData(img, 0, 0);
  return Texture.from(c);
}

export function getTrailTextures(skinId: string): TrailTextureSet {
  const hit = TRAIL_TEX_CACHE.get(skinId);
  if (hit) return hit;
  const set: TrailTextureSet = {
    outer: bakeStrip(256, 26),
    mid: bakeStrip(256, 14),
    core: bakeStrip(256, 6),
  };
  TRAIL_TEX_CACHE.set(skinId, set);
  return set;
}

export const TRAIL_SKINS: TrailSkin[] = [
  { id: 'comet',   name: 'COMÈTE',      cost: 0,   outer: 0xff6600, mid: 0xffaa00, core: 0xffffff },
  { id: 'rainbow', name: 'ARC-EN-CIEL', cost: 30,  outer: 0xff0066, mid: 0x44ff00, core: 0x00aaff },
  { id: 'ice',     name: 'GLACE',       cost: 75,  outer: 0x4488ff, mid: 0x88ddff, core: 0xffffff },
  { id: 'void',    name: 'NÉANT',       cost: 150, outer: 0x440088, mid: 0x8844ff, core: 0xff44ff },
  { id: 'dragon',  name: 'DRAGON',      cost: 300, outer: 0xff2200, mid: 0xff8844, core: 0xffff88 },
];

const OWNED_KEY = 'ns_trail_owned_pixi';
const ACTIVE_KEY = 'ns_trail_active_pixi';

export function getOwnedTrails(): Set<string> {
  try {
    const raw = JSON.parse(localStorage.getItem(OWNED_KEY) || '["comet"]');
    return new Set(raw);
  } catch { return new Set(['comet']); }
}
export function buyTrail(id: string): boolean {
  const skin = TRAIL_SKINS.find((s) => s.id === id);
  if (!skin) return false;
  const owned = getOwnedTrails();
  if (owned.has(id)) return false;
  owned.add(id);
  localStorage.setItem(OWNED_KEY, JSON.stringify(Array.from(owned)));
  return true;
}
export function getActiveTrailId(): string {
  return localStorage.getItem(ACTIVE_KEY) || 'comet';
}
export function setActiveTrailId(id: string): void {
  localStorage.setItem(ACTIVE_KEY, id);
}
export function getActiveTrail(): TrailSkin {
  return TRAIL_SKINS.find((s) => s.id === getActiveTrailId()) || TRAIL_SKINS[0];
}
